import React, { useState, useEffect, useCallback } from 'react';
import {
    Cpu,
    CheckCheck,
    Clock,
    AlertTriangle,
    ChevronDown,
    ChevronUp,
    RotateCcw,
    Play,
    Pause,
    Check,
    X,
    FileText,
    Shield,
    ShieldAlert,
    Activity,
    Info,
    TrendingUp,
    TrendingDown,
} from 'lucide-react';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassTabs } from '../ui/GlassTabs';
import { GlassBadge } from '../ui/GlassBadge';
import { GlassModal } from '../ui/GlassModal';
import { ModuleHeader } from '../ui/ModuleHeader';
import {
    getAutonomySettings,
    updateAutonomySettings,
    pauseAgent,
    resumeAgent,
    getApprovalQueue,
    approveQueueItem,
    rejectQueueItem,
    getAutonomyLog,
    rollbackAction,
    getSafetyStatus,
} from '../../services/api';
import './Autonomy.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Mode = 'off' | 'review' | 'auto';

interface AutonomySettings {
    settings_id: string;
    user_id: string;
    content_creation: Mode;
    content_publishing: Mode;
    social_scheduling: Mode;
    email_sending: Mode;
    lead_follow_up: Mode;
    campaign_launch: Mode;
    campaign_adjustment: Mode;
    max_contacts_without_approval: number;
    max_posts_per_day: number;
    alert_on_performance_drop_pct: number;
    agent_paused: boolean;
    paused_until: string | null;
    updated_at: string;
}

interface QueueItem {
    item_id: string;
    action_category: string;
    action_type: string;
    title: string;
    description: string;
    preview_data: any;
    status: string;
    created_at: string;
    expires_at: string | null;
}

interface OutcomeDelta {
    page_views?: number;
    leads_captured?: number;
    conversions?: number;
    form_submissions?: number;
}

interface OutcomeData {
    window_days?: number;
    delta?: OutcomeDelta;
    leads_in_window?: number;
    [key: string]: any;
}

interface LogEntry {
    log_id: string;
    action_category: string;
    action_type: string;
    title: string;
    mode: string;
    result: string;
    rollback_data: any | null;
    created_at: string;
    rolled_back_at: string | null;
    project_id?: string;
    outcome_status?: 'pending' | 'measured' | 'no_data' | 'not_applicable';
    outcome_data?: OutcomeData | null;
    measured_at?: string | null;
}

interface SafetyStatus {
    agent_paused: boolean;
    paused_until: string | null;
    posts_today: number;
    max_posts_per_day: number;
    pending_approvals: number;
    auto_actions_this_week: number;
    rolled_back_this_week: number;
    blast_radius_breaches_this_week: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ACTION_CATEGORIES: { key: keyof AutonomySettings; label: string; desc: string }[] = [
    { key: 'content_creation', label: 'Content Creation', desc: 'Blog posts, articles, social copy' },
    { key: 'content_publishing', label: 'Content Publishing', desc: 'Publish to WordPress or other platforms' },
    { key: 'social_scheduling', label: 'Social Scheduling', desc: 'Schedule posts to LinkedIn, X, Instagram' },
    { key: 'email_sending', label: 'Email Sending', desc: 'Send emails to leads or lists' },
    { key: 'lead_follow_up', label: 'Lead Follow-Up', desc: 'Enrol leads in nurture sequences' },
    { key: 'campaign_launch', label: 'Campaign Launch', desc: 'Launch new marketing campaigns' },
    { key: 'campaign_adjustment', label: 'Campaign Adjustment', desc: 'Modify budgets or targeting mid-flight' },
];

const TABS = [
    { id: 'control', label: 'Control Panel' },
    { id: 'queue', label: 'Approval Queue' },
    { id: 'log', label: 'Action Log' },
    { id: 'safety', label: 'Safety' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return 'just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString();
}

function formatDateTime(dateStr: string): string {
    return new Date(dateStr).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function modeBadgeVariant(mode: string): 'warning' | 'info' | 'default' {
    if (mode === 'auto') return 'warning';
    if (mode === 'review_approved') return 'success' as any;
    if (mode === 'review_rejected') return 'danger' as any;
    return 'default';
}

function resultBadgeVariant(result: string): 'success' | 'danger' | 'warning' | 'default' {
    if (result === 'success') return 'success';
    if (result === 'failed') return 'danger';
    if (result === 'rolled_back') return 'warning';
    return 'default';
}

function categoryLabel(cat: string): string {
    return (
        ACTION_CATEGORIES.find(c => c.key === cat)?.label ||
        cat.replace(/_/g, ' ')
    );
}

// Structured preview for a queued Facebook Page Branding push
// (action_type === 'apply_social_branding') — preview_data is a
// SocialBrandingDraft shape (see social_routes._draft_to_dict). Replaces a
// raw JSON.stringify dump with the thumbnails/bio/target Page a reviewer
// actually needs to approve or reject an outward-facing, irreversible
// Graph API write. See specs/completed/facebook-page-branding-live-push.md.
function BrandingQueuePreview({ preview, title }: { preview: any; title: string }) {
    if (!preview || typeof preview !== 'object') return null;
    const pageMatch = title.match(/@([^\s]+)$/);
    const pageLabel = pageMatch ? `@${pageMatch[1]}` : title;
    return (
        <div className="autonomy-branding-preview">
            <div className="autonomy-branding-preview-target">
                Target Page: <strong>{pageLabel}</strong>
            </div>
            <div className="autonomy-branding-preview-images">
                <div className="autonomy-branding-preview-image-tile">
                    {preview.profile_image_url ? (
                        <img src={preview.profile_image_url} alt="Profile" />
                    ) : (
                        <span className="autonomy-branding-preview-image-empty">No profile image</span>
                    )}
                    <span>Profile</span>
                </div>
                <div className="autonomy-branding-preview-image-tile autonomy-branding-preview-image-tile-wide">
                    {preview.banner_image_url ? (
                        <img src={preview.banner_image_url} alt="Cover" />
                    ) : (
                        <span className="autonomy-branding-preview-image-empty">No cover image</span>
                    )}
                    <span>Cover</span>
                </div>
            </div>
            {preview.bio_text && (
                <div className="autonomy-branding-preview-bio">
                    <strong>About:</strong> {preview.bio_text}
                </div>
            )}
            <div className="autonomy-branding-preview-contact">
                {preview.contact_phone && <span>Phone: {preview.contact_phone}</span>}
                {preview.contact_email && <span>Email: {preview.contact_email}</span>}
                {!preview.contact_phone && !preview.contact_email && (
                    <span className="autonomy-branding-preview-image-empty">No contact details to push</span>
                )}
            </div>
        </div>
    );
}

// Measured-outcome chip for action-log rows (additive backend fields)
const OUTCOME_METRIC_LABELS: { key: keyof OutcomeDelta; label: string }[] = [
    { key: 'page_views', label: 'page views' },
    { key: 'leads_captured', label: 'leads' },
    { key: 'conversions', label: 'conversions' },
    { key: 'form_submissions', label: 'form submissions' },
];

function renderOutcomeChip(entry: LogEntry): React.ReactNode {
    const status = entry.outcome_status;
    if (!status || status === 'not_applicable') return null;

    if (status === 'pending') {
        return (
            <span className="autonomy-outcome-chip">
                <Clock size={14} />
                measuring…
            </span>
        );
    }

    if (status === 'no_data') {
        return (
            <span className="autonomy-outcome-chip">
                <TrendingDown size={14} />
                no metrics data
            </span>
        );
    }

    // measured
    const delta = entry.outcome_data?.delta || {};
    const windowDays = entry.outcome_data?.window_days;
    const parts: string[] = [];
    let net = 0;
    for (const { key, label } of OUTCOME_METRIC_LABELS) {
        const val = delta[key];
        if (typeof val !== 'number' || val === 0) continue;
        net += val;
        parts.push(`${val > 0 ? '+' : ''}${val.toLocaleString()} ${label}`);
    }

    if (parts.length === 0) {
        return (
            <span className="autonomy-outcome-chip">
                <TrendingDown size={14} />
                no lift measured
            </span>
        );
    }

    const text = parts.join(' · ') + (windowDays ? ` in ${windowDays}d` : '');
    const positive = net > 0;
    return (
        <span className={`autonomy-outcome-chip ${positive ? 'positive' : ''}`}>
            {positive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {text}
        </span>
    );
}

// ---------------------------------------------------------------------------
// ModeSelector — 3-state segmented control
// ---------------------------------------------------------------------------

interface ModeSelectorProps {
    value: Mode;
    onChange: (mode: Mode) => void;
    disabled?: boolean;
}

const ModeSelector: React.FC<ModeSelectorProps> = ({ value, onChange, disabled }) => {
    const modes: { key: Mode; label: string }[] = [
        { key: 'off', label: 'Off' },
        { key: 'review', label: 'Review' },
        { key: 'auto', label: 'Auto' },
    ];

    return (
        <div className="autonomy-mode-selector">
            {modes.map(m => (
                <button
                    key={m.key}
                    className={`autonomy-mode-btn ${value === m.key ? `active-${m.key}` : ''}`}
                    onClick={() => !disabled && onChange(m.key)}
                    disabled={disabled}
                    type="button"
                    title={
                        m.key === 'off'
                            ? 'Disabled — agent will not act in this category'
                            : m.key === 'review'
                            ? 'Review mode — agent proposes, you approve'
                            : 'Auto mode — agent acts immediately, all actions logged'
                    }
                >
                    {m.label}
                </button>
            ))}
        </div>
    );
};

// ---------------------------------------------------------------------------
// AutonomyModule
// ---------------------------------------------------------------------------

const AutonomyModule: React.FC = () => {
    const [activeTab, setActiveTab] = useState('control');
    const [settings, setSettings] = useState<AutonomySettings | null>(null);
    const [pendingSettings, setPendingSettings] = useState<Partial<AutonomySettings>>({});
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [pauseLoading, setPauseLoading] = useState(false);
    const [showAutoWarning, setShowAutoWarning] = useState(false);

    const [queue, setQueue] = useState<QueueItem[]>([]);
    const [queueLoading, setQueueLoading] = useState(false);
    const [expandedPreviews, setExpandedPreviews] = useState<Set<string>>(new Set());
    const [rejectingItems, setRejectingItems] = useState<Record<string, string>>({}); // item_id → note
    const [showRejectInput, setShowRejectInput] = useState<Set<string>>(new Set());
    const [actionLoading, setActionLoading] = useState<Set<string>>(new Set());

    const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
    const [logLoading, setLogLoading] = useState(false);
    const [logCategoryFilter, setLogCategoryFilter] = useState('');
    const [logResultFilter, setLogResultFilter] = useState('');

    const [rollbackTarget, setRollbackTarget] = useState<LogEntry | null>(null);
    const [rollbackLoading, setRollbackLoading] = useState(false);
    const [rollbackResult, setRollbackResult] = useState<any | null>(null);

    const [safetyStatus, setSafetyStatus] = useState<SafetyStatus | null>(null);
    const [safetyLoading, setSafetyLoading] = useState(false);

    // ── Load settings ──────────────────────────────────────────────────────

    const loadSettings = useCallback(async () => {
        try {
            const data = await getAutonomySettings();
            setSettings(data);
            setPendingSettings({});
        } catch (err) {
            console.error('Failed to load autonomy settings', err);
        }
    }, []);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    // ── Load queue ─────────────────────────────────────────────────────────

    const loadQueue = useCallback(async () => {
        setQueueLoading(true);
        try {
            const data = await getApprovalQueue('pending');
            setQueue(data);
        } catch (err) {
            console.error('Failed to load approval queue', err);
        } finally {
            setQueueLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'queue') {
            loadQueue();
        }
    }, [activeTab, loadQueue]);

    // ── Load log ───────────────────────────────────────────────────────────

    const loadLog = useCallback(async () => {
        setLogLoading(true);
        try {
            const params: any = { limit: 50 };
            if (logCategoryFilter) params.action_category = logCategoryFilter;
            if (logResultFilter) params.result = logResultFilter;
            const data = await getAutonomyLog(params);
            setLogEntries(data);
        } catch (err) {
            console.error('Failed to load action log', err);
        } finally {
            setLogLoading(false);
        }
    }, [logCategoryFilter, logResultFilter]);

    useEffect(() => {
        if (activeTab === 'log') {
            loadLog();
        }
    }, [activeTab, loadLog]);

    // ── Load safety status ─────────────────────────────────────────────────

    const loadSafetyStatus = useCallback(async () => {
        setSafetyLoading(true);
        try {
            const data = await getSafetyStatus();
            setSafetyStatus(data);
        } catch (err) {
            console.error('Failed to load safety status', err);
        } finally {
            setSafetyLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'safety') {
            loadSafetyStatus();
            // Also load the action log so we can show recent rollbacks
            if (logEntries.length === 0) {
                loadLog();
            }
        }
    }, [activeTab, loadSafetyStatus, loadLog, logEntries.length]);

    // ── Control Panel handlers ─────────────────────────────────────────────

    const mergedSettings = settings
        ? { ...settings, ...pendingSettings }
        : null;

    const handleModeChange = (category: keyof AutonomySettings, mode: Mode) => {
        setPendingSettings(prev => ({ ...prev, [category]: mode }));
        // Show auto warning if any category is set to auto
        const updatedModes = { ...(mergedSettings || {}), [category]: mode };
        const anyAuto = ACTION_CATEGORIES.some(
            c => (updatedModes as any)[c.key] === 'auto'
        );
        setShowAutoWarning(anyAuto);
    };

    const handleThresholdChange = (field: keyof AutonomySettings, value: number) => {
        setPendingSettings(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        if (!settings || Object.keys(pendingSettings).length === 0) return;
        setSaveLoading(true);
        try {
            const data = await updateAutonomySettings(pendingSettings);
            setSettings(data);
            setPendingSettings({});
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (err) {
            console.error('Failed to save settings', err);
        } finally {
            setSaveLoading(false);
        }
    };

    const handlePauseToggle = async () => {
        if (!settings) return;
        setPauseLoading(true);
        try {
            if (settings.agent_paused) {
                await resumeAgent();
            } else {
                await pauseAgent();
            }
            await loadSettings();
        } catch (err) {
            console.error('Failed to toggle pause', err);
        } finally {
            setPauseLoading(false);
        }
    };

    // ── Queue handlers ─────────────────────────────────────────────────────

    const togglePreview = (itemId: string) => {
        setExpandedPreviews(prev => {
            const next = new Set(prev);
            if (next.has(itemId)) next.delete(itemId);
            else next.add(itemId);
            return next;
        });
    };

    const handleApprove = async (itemId: string) => {
        setActionLoading(prev => new Set(prev).add(itemId));
        try {
            await approveQueueItem(itemId);
            setQueue(prev => prev.filter(i => i.item_id !== itemId));
        } catch (err) {
            console.error('Failed to approve queue item', err);
        } finally {
            setActionLoading(prev => {
                const next = new Set(prev);
                next.delete(itemId);
                return next;
            });
        }
    };

    const handleShowReject = (itemId: string) => {
        setShowRejectInput(prev => {
            const next = new Set(prev);
            next.add(itemId);
            return next;
        });
    };

    const handleReject = async (itemId: string) => {
        setActionLoading(prev => new Set(prev).add(itemId));
        try {
            const note = rejectingItems[itemId] || undefined;
            await rejectQueueItem(itemId, note);
            setQueue(prev => prev.filter(i => i.item_id !== itemId));
            setShowRejectInput(prev => {
                const next = new Set(prev);
                next.delete(itemId);
                return next;
            });
        } catch (err) {
            console.error('Failed to reject queue item', err);
        } finally {
            setActionLoading(prev => {
                const next = new Set(prev);
                next.delete(itemId);
                return next;
            });
        }
    };

    // ── Rollback handlers ──────────────────────────────────────────────────

    const handleRollback = async () => {
        if (!rollbackTarget) return;
        setRollbackLoading(true);
        try {
            const result = await rollbackAction(rollbackTarget.log_id);
            setLogEntries(prev =>
                prev.map(e =>
                    e.log_id === rollbackTarget.log_id
                        ? { ...e, result: 'rolled_back' }
                        : e
                )
            );
            setRollbackResult(result?.rollback_result || null);
            setRollbackTarget(null);
        } catch (err) {
            console.error('Rollback failed', err);
        } finally {
            setRollbackLoading(false);
        }
    };

    // ── Pending queue count (for tab badge) ───────────────────────────────

    const pendingCount = queue.length;

    const tabsWithBadge = TABS.map(t =>
        t.id === 'queue' && pendingCount > 0
            ? { ...t, label: `Approval Queue (${pendingCount})` }
            : t
    );

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------

    return (
        <div className="autonomy-module">
            <ModuleHeader
                title="Autonomy"
                breadcrumb={['H4', 'Autonomy']}
                actions={[
                    <GlassTabs
                        key="tabs"
                        tabs={tabsWithBadge}
                        activeTab={activeTab}
                        onChange={setActiveTab}
                        size="sm"
                    />,
                ]}
            />

            {/* ── TAB 1: Control Panel ───────────────────────────────────── */}
            {activeTab === 'control' && (
                <>
                    {/* Agent Status */}
                    <GlassCard variant="strong" padding="lg">
                        <div className="autonomy-status-card">
                            <div className="autonomy-status-indicator">
                                <span
                                    className={`autonomy-status-dot ${
                                        settings?.agent_paused ? 'paused' : 'active'
                                    }`}
                                />
                                <div>
                                    <div className="autonomy-status-label">
                                        Agent {settings?.agent_paused ? 'Paused' : 'Active'}
                                    </div>
                                    <div className="autonomy-status-sub">
                                        {settings?.agent_paused
                                            ? 'All actions are queued for your review while the agent is paused'
                                            : 'Agent is running — actions follow per-category mode settings below'}
                                    </div>
                                </div>
                            </div>
                            <div className="autonomy-status-actions">
                                <GlassButton
                                    variant={settings?.agent_paused ? 'primary' : 'secondary'}
                                    size="sm"
                                    loading={pauseLoading}
                                    onClick={handlePauseToggle}
                                    icon={settings?.agent_paused ? <Play size={14} /> : <Pause size={14} />}
                                >
                                    {settings?.agent_paused ? 'Resume Agent' : 'Pause Agent'}
                                </GlassButton>
                            </div>
                        </div>
                    </GlassCard>

                    {/* Auto warning banner */}
                    {showAutoWarning && (
                        <div className="autonomy-auto-warning">
                            <span className="autonomy-auto-warning-icon">
                                <AlertTriangle size={16} />
                            </span>
                            <span>
                                <strong>Auto mode is active.</strong> AiMe will act without asking you first.
                                All actions are logged and can be rolled back from the Action Log tab.
                            </span>
                        </div>
                    )}

                    {/* Category toggles */}
                    <GlassCard variant="default" padding="md">
                        <div className="autonomy-categories-section">
                            <h3>Action Category Settings</h3>
                            {ACTION_CATEGORIES.map(cat => (
                                <div key={cat.key} className="autonomy-category-row">
                                    <div className="autonomy-category-info">
                                        <div className="autonomy-category-name">{cat.label}</div>
                                        <div className="autonomy-category-desc">{cat.desc}</div>
                                    </div>
                                    <ModeSelector
                                        value={
                                            ((mergedSettings as any)?.[cat.key] as Mode) || 'review'
                                        }
                                        onChange={mode => handleModeChange(cat.key, mode)}
                                    />
                                </div>
                            ))}
                        </div>
                    </GlassCard>

                    {/* Escalation thresholds */}
                    <GlassCard variant="default" padding="md">
                        <div className="autonomy-thresholds-section">
                            <h3>Escalation Thresholds</h3>
                            <div className="autonomy-thresholds-grid">
                                <div className="autonomy-threshold-field">
                                    <label className="autonomy-threshold-label">
                                        Max contacts without approval
                                    </label>
                                    <span className="autonomy-threshold-desc">
                                        Email sends above this count require approval regardless of mode
                                    </span>
                                    <input
                                        type="number"
                                        className="autonomy-threshold-input"
                                        min={1}
                                        max={10000}
                                        value={
                                            (pendingSettings.max_contacts_without_approval ??
                                                settings?.max_contacts_without_approval) ||
                                            50
                                        }
                                        onChange={e =>
                                            handleThresholdChange(
                                                'max_contacts_without_approval',
                                                parseInt(e.target.value, 10) || 50
                                            )
                                        }
                                    />
                                </div>
                                <div className="autonomy-threshold-field">
                                    <label className="autonomy-threshold-label">
                                        Max posts per day
                                    </label>
                                    <span className="autonomy-threshold-desc">
                                        Social posts above this daily count require approval
                                    </span>
                                    <input
                                        type="number"
                                        className="autonomy-threshold-input"
                                        min={1}
                                        max={100}
                                        value={
                                            (pendingSettings.max_posts_per_day ??
                                                settings?.max_posts_per_day) ||
                                            3
                                        }
                                        onChange={e =>
                                            handleThresholdChange(
                                                'max_posts_per_day',
                                                parseInt(e.target.value, 10) || 3
                                            )
                                        }
                                    />
                                </div>
                                <div className="autonomy-threshold-field">
                                    <label className="autonomy-threshold-label">
                                        Alert on performance drop (%)
                                    </label>
                                    <span className="autonomy-threshold-desc">
                                        Alert when a campaign KPI drops by this percentage
                                    </span>
                                    <input
                                        type="number"
                                        className="autonomy-threshold-input"
                                        min={1}
                                        max={100}
                                        value={
                                            (pendingSettings.alert_on_performance_drop_pct ??
                                                settings?.alert_on_performance_drop_pct) ||
                                            20
                                        }
                                        onChange={e =>
                                            handleThresholdChange(
                                                'alert_on_performance_drop_pct',
                                                parseInt(e.target.value, 10) || 20
                                            )
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </GlassCard>

                    {/* Save row */}
                    <div className="autonomy-save-row">
                        {saveSuccess && (
                            <span className="autonomy-save-success">
                                Settings saved
                            </span>
                        )}
                        <GlassButton
                            variant="primary"
                            size="md"
                            loading={saveLoading}
                            onClick={handleSave}
                            disabled={Object.keys(pendingSettings).length === 0}
                        >
                            Save Settings
                        </GlassButton>
                    </div>
                </>
            )}

            {/* ── TAB 2: Approval Queue ──────────────────────────────────── */}
            {activeTab === 'queue' && (
                <>
                    {queueLoading ? (
                        <GlassCard variant="default" padding="lg">
                            <div className="autonomy-queue-empty">
                                <span className="autonomy-queue-empty-text">Loading...</span>
                            </div>
                        </GlassCard>
                    ) : queue.length === 0 ? (
                        <GlassCard variant="default" padding="lg">
                            <div className="autonomy-queue-empty">
                                <span className="autonomy-queue-empty-icon">
                                    <CheckCheck size={40} />
                                </span>
                                <span className="autonomy-queue-empty-text">
                                    No pending actions — you're all caught up
                                </span>
                                <span className="autonomy-queue-empty-sub">
                                    When AiMe wants to take an action in Review mode, it will appear here
                                </span>
                            </div>
                        </GlassCard>
                    ) : (
                        <div className="autonomy-queue-list">
                            {queue.map(item => (
                                <GlassCard
                                    key={item.item_id}
                                    variant="default"
                                    padding="md"
                                    className="autonomy-queue-item-card"
                                >
                                    {/* Header row */}
                                    <div className="autonomy-queue-item-header">
                                        <div className="autonomy-queue-item-meta">
                                            <GlassBadge variant="info" size="sm">
                                                {categoryLabel(item.action_category)}
                                            </GlassBadge>
                                            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                                                {item.action_type.replace(/_/g, ' ')}
                                            </span>
                                        </div>
                                        <span className="autonomy-queue-item-time">
                                            <Clock size={12} style={{ display: 'inline', marginRight: 4 }} />
                                            {timeAgo(item.created_at)}
                                        </span>
                                    </div>

                                    {/* Title & description */}
                                    <p className="autonomy-queue-item-title">{item.title}</p>
                                    <p className="autonomy-queue-item-desc">{item.description}</p>

                                    {/* Preview toggle */}
                                    {item.preview_data && (
                                        <>
                                            <button
                                                className="autonomy-preview-toggle"
                                                onClick={() => togglePreview(item.item_id)}
                                                type="button"
                                            >
                                                <FileText size={14} />
                                                {expandedPreviews.has(item.item_id)
                                                    ? 'Hide preview'
                                                    : 'Show preview'}
                                                {expandedPreviews.has(item.item_id) ? (
                                                    <ChevronUp size={12} />
                                                ) : (
                                                    <ChevronDown size={12} />
                                                )}
                                            </button>
                                            {expandedPreviews.has(item.item_id) && (
                                                item.action_type === 'apply_social_branding' ? (
                                                    <BrandingQueuePreview preview={item.preview_data} title={item.title} />
                                                ) : (
                                                    <pre className="autonomy-preview-block">
                                                        {typeof item.preview_data === 'string'
                                                            ? item.preview_data
                                                            : JSON.stringify(item.preview_data, null, 2)}
                                                    </pre>
                                                )
                                            )}
                                        </>
                                    )}

                                    {/* Reject inline input */}
                                    {showRejectInput.has(item.item_id) ? (
                                        <div className="autonomy-reject-row">
                                            <input
                                                type="text"
                                                className="autonomy-reject-input"
                                                placeholder="Reason for rejection (optional)"
                                                value={rejectingItems[item.item_id] || ''}
                                                onChange={e =>
                                                    setRejectingItems(prev => ({
                                                        ...prev,
                                                        [item.item_id]: e.target.value,
                                                    }))
                                                }
                                                autoFocus
                                            />
                                            <GlassButton
                                                variant="danger"
                                                size="sm"
                                                loading={actionLoading.has(item.item_id)}
                                                onClick={() => handleReject(item.item_id)}
                                                icon={<X size={14} />}
                                            >
                                                Confirm Reject
                                            </GlassButton>
                                            <GlassButton
                                                variant="secondary"
                                                size="sm"
                                                onClick={() =>
                                                    setShowRejectInput(prev => {
                                                        const next = new Set(prev);
                                                        next.delete(item.item_id);
                                                        return next;
                                                    })
                                                }
                                            >
                                                Cancel
                                            </GlassButton>
                                        </div>
                                    ) : (
                                        <div className="autonomy-queue-item-actions">
                                            <GlassButton
                                                variant="primary"
                                                size="sm"
                                                loading={actionLoading.has(item.item_id)}
                                                onClick={() => handleApprove(item.item_id)}
                                                icon={<Check size={14} />}
                                                style={{
                                                    background: 'var(--success)',
                                                    borderColor: 'var(--success)',
                                                }}
                                            >
                                                Approve
                                            </GlassButton>
                                            <GlassButton
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleShowReject(item.item_id)}
                                                icon={<X size={14} />}
                                            >
                                                Reject
                                            </GlassButton>
                                        </div>
                                    )}
                                </GlassCard>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* ── TAB 3: Action Log ─────────────────────────────────────── */}
            {activeTab === 'log' && (
                <>
                    {/* Filters */}
                    <div className="autonomy-log-filters">
                        <select
                            className="autonomy-log-filter-select"
                            value={logCategoryFilter}
                            onChange={e => setLogCategoryFilter(e.target.value)}
                        >
                            <option value="">All Categories</option>
                            {ACTION_CATEGORIES.map(c => (
                                <option key={c.key} value={c.key}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                        <select
                            className="autonomy-log-filter-select"
                            value={logResultFilter}
                            onChange={e => setLogResultFilter(e.target.value)}
                        >
                            <option value="">All Results</option>
                            <option value="success">Success</option>
                            <option value="failed">Failed</option>
                            <option value="rolled_back">Rolled Back</option>
                        </select>
                        <GlassButton
                            variant="secondary"
                            size="sm"
                            onClick={loadLog}
                            loading={logLoading}
                        >
                            Refresh
                        </GlassButton>
                    </div>

                    <GlassCard variant="default" padding="none">
                        {logLoading ? (
                            <p style={{ padding: 'var(--space-6)', color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
                                Loading...
                            </p>
                        ) : logEntries.length === 0 ? (
                            <p className="autonomy-log-empty">
                                No actions logged yet
                            </p>
                        ) : (
                            <table className="autonomy-log-table">
                                <thead>
                                    <tr>
                                        <th>Time</th>
                                        <th>Category</th>
                                        <th>Action</th>
                                        <th>Mode</th>
                                        <th>Result</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logEntries.map(entry => (
                                        <tr key={entry.log_id}>
                                            <td className="autonomy-log-time">
                                                {formatDateTime(entry.created_at)}
                                            </td>
                                            <td>
                                                <GlassBadge variant="default" size="sm">
                                                    {categoryLabel(entry.action_category)}
                                                </GlassBadge>
                                            </td>
                                            <td>
                                                <span className="autonomy-log-title" title={entry.title}>
                                                    {entry.title}
                                                </span>
                                                {renderOutcomeChip(entry)}
                                            </td>
                                            <td>
                                                <GlassBadge
                                                    variant={modeBadgeVariant(entry.mode) as any}
                                                    size="sm"
                                                >
                                                    {entry.mode.replace(/_/g, ' ')}
                                                </GlassBadge>
                                            </td>
                                            <td>
                                                <GlassBadge
                                                    variant={resultBadgeVariant(entry.result)}
                                                    size="sm"
                                                >
                                                    {entry.result}
                                                </GlassBadge>
                                            </td>
                                            <td>
                                                {entry.result !== 'rolled_back' &&
                                                    entry.result !== 'failed' && (
                                                        <GlassButton
                                                            variant="ghost"
                                                            size="sm"
                                                            icon={<RotateCcw size={14} />}
                                                            onClick={() => setRollbackTarget(entry)}
                                                            title="Rollback this action"
                                                        />
                                                    )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </GlassCard>
                </>
            )}

            {/* ── TAB 4: Safety ─────────────────────────────────────────── */}
            {activeTab === 'safety' && (
                <>
                    {safetyLoading ? (
                        <GlassCard variant="default" padding="lg">
                            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
                                Loading safety status...
                            </p>
                        </GlassCard>
                    ) : (
                        <>
                            {/* Safety Status card */}
                            <GlassCard variant="strong" padding="lg">
                                <div className="autonomy-safety-header">
                                    <Shield size={20} style={{ color: 'var(--primary-400)', flexShrink: 0 }} />
                                    <div>
                                        <h3 className="autonomy-safety-title">Safety Status</h3>
                                        <p className="autonomy-safety-sub">Live safety metrics for autonomous operations</p>
                                    </div>
                                    <GlassButton
                                        variant="secondary"
                                        size="sm"
                                        onClick={loadSafetyStatus}
                                        loading={safetyLoading}
                                    >
                                        Refresh
                                    </GlassButton>
                                </div>

                                <div className="autonomy-safety-stats">
                                    {/* Agent status */}
                                    <div className="autonomy-safety-stat">
                                        <div className="autonomy-safety-stat-label">Agent Status</div>
                                        <div className="autonomy-safety-stat-value">
                                            <span
                                                className={`autonomy-status-dot ${
                                                    safetyStatus?.agent_paused ? 'paused' : 'active'
                                                }`}
                                                style={{ display: 'inline-block' }}
                                            />
                                            <span style={{ marginLeft: 8 }}>
                                                {safetyStatus?.agent_paused ? 'Paused' : 'Active'}
                                            </span>
                                        </div>
                                        {safetyStatus?.paused_until && (
                                            <div className="autonomy-safety-stat-sub">
                                                Until {formatDateTime(safetyStatus.paused_until)}
                                            </div>
                                        )}
                                    </div>

                                    {/* Posts today */}
                                    <div className="autonomy-safety-stat">
                                        <div className="autonomy-safety-stat-label">Auto-Posts Today</div>
                                        <div className="autonomy-safety-stat-value">
                                            {safetyStatus?.posts_today ?? 0} / {safetyStatus?.max_posts_per_day ?? 3}
                                        </div>
                                        <div className="autonomy-safety-progress-bar">
                                            <div
                                                className="autonomy-safety-progress-fill"
                                                style={{
                                                    width: `${Math.min(
                                                        100,
                                                        ((safetyStatus?.posts_today ?? 0) /
                                                            (safetyStatus?.max_posts_per_day || 1)) * 100
                                                    )}%`,
                                                    background:
                                                        (safetyStatus?.posts_today ?? 0) >=
                                                        (safetyStatus?.max_posts_per_day ?? 3)
                                                            ? 'var(--danger)'
                                                            : 'var(--primary-400)',
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Pending approvals */}
                                    <div className="autonomy-safety-stat">
                                        <div className="autonomy-safety-stat-label">Pending Approvals</div>
                                        <div className="autonomy-safety-stat-value">
                                            {safetyStatus?.pending_approvals ?? 0}
                                        </div>
                                        {(safetyStatus?.pending_approvals ?? 0) > 0 && (
                                            <GlassButton
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => setActiveTab('queue')}
                                                style={{ marginTop: 'var(--space-2)' }}
                                            >
                                                Review
                                            </GlassButton>
                                        )}
                                    </div>

                                    {/* Auto actions this week */}
                                    <div className="autonomy-safety-stat">
                                        <div className="autonomy-safety-stat-label">Auto Actions (7d)</div>
                                        <div className="autonomy-safety-stat-value">
                                            {safetyStatus?.auto_actions_this_week ?? 0}
                                        </div>
                                    </div>

                                    {/* Rolled back this week */}
                                    <div className="autonomy-safety-stat">
                                        <div className="autonomy-safety-stat-label">Rolled Back (7d)</div>
                                        <div className="autonomy-safety-stat-value">
                                            {safetyStatus?.rolled_back_this_week ?? 0}
                                        </div>
                                    </div>

                                    {/* Blast radius breaches */}
                                    <div className="autonomy-safety-stat">
                                        <div className="autonomy-safety-stat-label">Blast Radius Blocks (7d)</div>
                                        <div className="autonomy-safety-stat-value">
                                            {safetyStatus?.blast_radius_breaches_this_week ?? 0}
                                        </div>
                                    </div>
                                </div>
                            </GlassCard>

                            {/* Blast Radius Limits */}
                            <GlassCard variant="default" padding="md">
                                <div className="autonomy-safety-section-header">
                                    <ShieldAlert size={18} style={{ color: 'var(--warning)' }} />
                                    <h3>Blast Radius Limits</h3>
                                </div>
                                <p className="autonomy-safety-section-desc">
                                    These hard caps are enforced before any autonomous action executes, regardless of mode setting.
                                </p>
                                <div className="autonomy-safety-limits">
                                    <div className="autonomy-safety-limit-row">
                                        <span className="autonomy-safety-limit-label">Max contacts per email action</span>
                                        <span className="autonomy-safety-limit-value">
                                            {settings?.max_contacts_without_approval ?? 50}
                                        </span>
                                    </div>
                                    <div className="autonomy-safety-limit-row">
                                        <span className="autonomy-safety-limit-label">Max auto-posts per day</span>
                                        <span className="autonomy-safety-limit-value">
                                            {settings?.max_posts_per_day ?? 3}
                                        </span>
                                    </div>
                                    <div className="autonomy-safety-limit-row">
                                        <span className="autonomy-safety-limit-label">Performance drop alert threshold</span>
                                        <span className="autonomy-safety-limit-value">
                                            {settings?.alert_on_performance_drop_pct ?? 20}%
                                        </span>
                                    </div>
                                </div>
                                <div style={{ marginTop: 'var(--space-4)' }}>
                                    <GlassButton
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setActiveTab('control')}
                                    >
                                        Edit Limits
                                    </GlassButton>
                                </div>
                            </GlassCard>

                            {/* Safety Log — recent rolled-back actions */}
                            <GlassCard variant="default" padding="md">
                                <div className="autonomy-safety-section-header">
                                    <Activity size={18} style={{ color: 'var(--text-secondary)' }} />
                                    <h3>Recent Rollbacks</h3>
                                </div>
                                <p className="autonomy-safety-section-desc">
                                    Last 5 actions that were rolled back.
                                </p>
                                {logEntries.filter(e => e.result === 'rolled_back').slice(0, 5).length === 0 ? (
                                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginTop: 'var(--space-3)' }}>
                                        No rolled-back actions yet.
                                    </p>
                                ) : (
                                    <div className="autonomy-safety-rollback-list">
                                        {logEntries
                                            .filter(e => e.result === 'rolled_back')
                                            .slice(0, 5)
                                            .map(entry => (
                                                <div key={entry.log_id} className="autonomy-safety-rollback-row">
                                                    <span className="autonomy-safety-rollback-time">
                                                        {formatDateTime(entry.rolled_back_at || entry.created_at)}
                                                    </span>
                                                    <span className="autonomy-safety-rollback-title">
                                                        {entry.title}
                                                    </span>
                                                    <GlassBadge variant="warning" size="sm">
                                                        rolled back
                                                    </GlassBadge>
                                                </div>
                                            ))}
                                    </div>
                                )}
                                <div style={{ marginTop: 'var(--space-4)' }}>
                                    <GlassButton
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setActiveTab('log')}
                                    >
                                        View Full Log
                                    </GlassButton>
                                </div>
                            </GlassCard>

                            {/* Agent Reasoning — how graceful failure works */}
                            <GlassCard variant="default" padding="md">
                                <div className="autonomy-safety-section-header">
                                    <Info size={18} style={{ color: 'var(--info)' }} />
                                    <h3>Agent Reasoning & Graceful Failure</h3>
                                </div>
                                <p className="autonomy-safety-section-desc">
                                    When AiMe is uncertain, it always asks before acting. It will never guess when the stakes are high.
                                </p>
                                <div className="autonomy-safety-reasoning-list">
                                    <div className="autonomy-safety-reasoning-item">
                                        <AlertTriangle size={14} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                                        <span>Blast radius limit exceeded — action contacts or post count would exceed your configured thresholds</span>
                                    </div>
                                    <div className="autonomy-safety-reasoning-item">
                                        <AlertTriangle size={14} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                                        <span>Conflicting signals — the data the agent has is ambiguous or incomplete</span>
                                    </div>
                                    <div className="autonomy-safety-reasoning-item">
                                        <AlertTriangle size={14} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                                        <span>Irreversible action — the proposed action cannot easily be undone (e.g. sending emails to a large list)</span>
                                    </div>
                                    <div className="autonomy-safety-reasoning-item">
                                        <AlertTriangle size={14} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                                        <span>Agent paused — all autonomous actions queue for review regardless of category settings</span>
                                    </div>
                                    <div className="autonomy-safety-reasoning-item">
                                        <AlertTriangle size={14} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                                        <span>Category set to Review mode — the specific action category requires your explicit approval</span>
                                    </div>
                                </div>
                            </GlassCard>
                        </>
                    )}
                </>
            )}

            {/* ── Rollback Confirm Modal ─────────────────────────────────── */}
            {rollbackTarget && (
                <GlassModal
                    isOpen={!!rollbackTarget}
                    onClose={() => setRollbackTarget(null)}
                    title="Confirm Rollback"
                    size="sm"
                >
                    <div className="autonomy-rollback-dialog">
                        <p className="autonomy-rollback-warning">
                            This will attempt to roll back <strong>{rollbackTarget.title}</strong>.
                            {rollbackTarget.rollback_data
                                ? ' Automatic rollback data is available — AiMe will attempt to undo the action.'
                                : ' No rollback data was recorded — you may need to undo changes manually.'}
                        </p>
                        <div className="autonomy-rollback-actions">
                            <GlassButton
                                variant="secondary"
                                size="sm"
                                onClick={() => setRollbackTarget(null)}
                            >
                                Cancel
                            </GlassButton>
                            <GlassButton
                                variant="danger"
                                size="sm"
                                loading={rollbackLoading}
                                onClick={handleRollback}
                                icon={<RotateCcw size={14} />}
                            >
                                Confirm Rollback
                            </GlassButton>
                        </div>
                    </div>
                </GlassModal>
            )}

            {/* ── Rollback Result Modal ──────────────────────────────────── */}
            {rollbackResult && (
                <GlassModal
                    isOpen={!!rollbackResult}
                    onClose={() => setRollbackResult(null)}
                    title="Rollback Result"
                    size="sm"
                >
                    <div className="autonomy-rollback-dialog">
                        {rollbackResult.steps?.map((step: string, i: number) => (
                            <p key={i} className="autonomy-rollback-warning">
                                {step}
                            </p>
                        ))}
                        {rollbackResult.manual_required && (
                            <div className="autonomy-auto-warning" style={{ marginTop: 'var(--space-3)' }}>
                                <span className="autonomy-auto-warning-icon">
                                    <AlertTriangle size={16} />
                                </span>
                                <span>Manual intervention may be required. Check the affected module.</span>
                            </div>
                        )}
                        <div className="autonomy-rollback-actions">
                            <GlassButton
                                variant="primary"
                                size="sm"
                                onClick={() => setRollbackResult(null)}
                            >
                                Done
                            </GlassButton>
                        </div>
                    </div>
                </GlassModal>
            )}
        </div>
    );
};

export default AutonomyModule;
